import { useState } from "react";
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { Close, InfoOutlined } from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatDisplayHours } from "../utils/general";
import {
    formatRemunerationWeekdays,
    getVisiblePayLines,
    PayBasis,
    ServiceProviderPay,
    ServiceProviderPayLine,
} from "../utils/serviceProviderPay";
import { ContractPeriod } from "../utils/monthContractType";

type ServiceProviderPaySummaryProps = {
    pay: ServiceProviderPay;
    isMixedMonth: boolean;
    contractPeriods: Array<ContractPeriod>;
    agreementTypeLabels: Record<string, string>;
};

const EMPTY_CELL = "—";

const BASIS_LABELS: Record<PayBasis, string> = {
    hourly: "Por hora",
    perClient: "Por cliente",
    hourAndClient: "Hora e cliente",
    none: EMPTY_CELL,
};

const formatDateKey = (dateKey: string) => {
    const formattedDate = format(parseISO(dateKey), "dd/MM/yyyy");
    return formattedDate;
};

const formatPeriodLabel = (period: ContractPeriod, agreementTypeLabels: Record<string, string>) => {
    const typeLabel = agreementTypeLabels[period.agreementType] ?? period.agreementType;
    const label = `${typeLabel} · ${formatDateKey(period.startDate)} a ${formatDateKey(period.endDate)}`;
    return label;
};

const formatLineHours = (line: ServiceProviderPayLine) => {
    const hoursByBasis: Record<PayBasis, string> = {
        hourly: formatDisplayHours(line.scheduleHours),
        perClient: formatDisplayHours(line.clientHours),
        hourAndClient: `${formatDisplayHours(line.nonClientHours)} + ${formatDisplayHours(line.clientHours)} cliente`,
        none: formatDisplayHours(line.scheduleHours),
    };
    const hours = hoursByBasis[line.basis];
    return hours;
};

const formatLineRate = (line: ServiceProviderPayLine) => {
    const rateByBasis: Record<PayBasis, string> = {
        hourly: formatCurrency(line.valuePerHour),
        perClient: formatCurrency(line.valuePerClient),
        hourAndClient: `${formatCurrency(line.valuePerHour)} + ${formatCurrency(line.valuePerClient)}`,
        none: EMPTY_CELL,
    };
    const rate = rateByBasis[line.basis];
    return rate;
};

export const ServiceProviderPaySummary = ({ pay, isMixedMonth, contractPeriods, agreementTypeLabels }: ServiceProviderPaySummaryProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const visibleLines = getVisiblePayLines(pay.lines);
    const hasUncoveredHours = visibleLines.some((line) => line.isUncovered);
    const showsValue = !pay.hasConflict && Math.round(pay.totalValue * 100) !== 0;

    if (!pay.hasConflict && !showsValue && !isMixedMonth) return null;

    return (
        <Box display="flex" flexDirection="column" alignItems="flex-start">
            {pay.hasConflict && (
                <Typography color="error">
                    Valor para recibo: remuneração em conflito (dois registos com a mesma data para o mesmo dia)
                </Typography>
            )}
            {showsValue && (
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography>Valor para recibo: {formatCurrency(pay.totalValue)}</Typography>
                    <Tooltip title={hasUncoveredHours ? "Há horas sem valor definido — ver detalhe" : "Ver detalhe"} arrow>
                        <IconButton
                            aria-label="Ver detalhe do valor para recibo"
                            size="small"
                            color={hasUncoveredHours ? "warning" : "primary"}
                            onClick={() => setIsOpen(true)}
                        >
                            <InfoOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
            {isMixedMonth && (
                <Link component="button" variant="body2" color="error" onClick={() => setIsOpen(true)}>
                    Mês com diferentes tipos de contrato
                </Link>
            )}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6" component="span">
                        Detalhe do valor para recibo
                    </Typography>
                    <IconButton onClick={() => setIsOpen(false)} size="small">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {isMixedMonth && (
                        <Box sx={{ mb: 2 }}>
                            {contractPeriods.map((period) => (
                                <Typography key={period.startDate} variant="body2">
                                    {formatPeriodLabel(period, agreementTypeLabels)}
                                </Typography>
                            ))}
                            {pay.contractScheduleHours > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {`Horas em contrato: ${formatDisplayHours(pay.contractScheduleHours)}, pagas no processamento salarial`}
                                </Typography>
                            )}
                        </Box>
                    )}
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Dias</TableCell>
                                    <TableCell>Desde</TableCell>
                                    <TableCell>Forma</TableCell>
                                    <TableCell align="right">Horas</TableCell>
                                    <TableCell align="right">Valor</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleLines.map((line) => (
                                    <TableRow key={line.remunerationId ?? "uncovered"}>
                                        <TableCell sx={{ color: line.isUncovered ? "warning.main" : undefined }}>
                                            {line.isUncovered ? "Sem valor definido" : formatRemunerationWeekdays(line.weekdays)}
                                        </TableCell>
                                        <TableCell>{line.startDate ? formatDateKey(line.startDate) : EMPTY_CELL}</TableCell>
                                        <TableCell>{line.isUncovered ? EMPTY_CELL : BASIS_LABELS[line.basis]}</TableCell>
                                        <TableCell align="right">{formatLineHours(line)}</TableCell>
                                        <TableCell align="right">{formatLineRate(line)}</TableCell>
                                        <TableCell align="right">{formatCurrency(line.value)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ fontWeight: 700 }}>
                                        Total
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: pay.hasConflict ? "error.main" : undefined }}>
                                        {pay.hasConflict ? "Remuneração em conflito" : formatCurrency(pay.totalValue)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>
        </Box>
    );
};
